import {
  CurriculumListItem,
  type CurriculumRole,
  CurriculumTagsView,
  type DocumentId,
  ResolveArgs,
  type ResolveDecision,
  ResolveSuccess,
} from "@curriculum-studio/core";
import { HttpClient, HttpClientRequest, HttpClientResponse } from "@effect/platform";
import { Data, Effect, Schema } from "effect";

// The REST half of the client (N8): the generic `POST /api/resolve` (the same `{ documentId,
// decision }` the MCP `resolve` op takes) and the read-only `GET /api/curricula` index. Both go
// through `@effect/platform`'s `HttpClient` (browser-backed by `FetchHttpClient`) so the IO stays
// in Effect, and both responses are decoded with `core` boundary `Schema`s — the same branded ids
// the server encodes. Tests substitute a stub `HttpClient` layer to assert the request shape.

// The success/list wire shapes are the shared `core` boundary Schemas (R5), so this client decodes
// exactly what the server encodes. Re-export the types for the gate context/components that consume
// them, preserving the prior import surface.
export type { CurriculumListItem } from "@curriculum-studio/core";

// A 409 means the decision lost the first-writer-wins race — the annotation was decided (or
// reclassified) between render and click (server `ResolveConflict`). Surfaced as a tagged error so
// the gate UI can show the conflict and resync to the now-authoritative document rather than
// swallowing the rejected promise (R7).
export class ResolveAlreadyDecided extends Data.TaggedError("ResolveAlreadyDecided")<{
  readonly message: string;
}> {}

// Any non-409 error response (network failure surfaced as 5xx, a 404, a 422) must be a *tagged*
// failure the UI can distinguish from a conflict, not fall through to a `ResolveSuccess` decode that
// would surface as an opaque `ParseError` (R15). The 409 conflict keeps its own dedicated error.
export class ResolveRequestFailed extends Data.TaggedError("ResolveRequestFailed")<{
  readonly status: number;
  readonly message: string;
}> {}

const CurriculaList = Schema.Array(CurriculumListItem);

export const resolve = (documentId: DocumentId, decision: ResolveDecision) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    // Encode the payload through the shared `ResolveArgs` boundary Schema (R19) so the branded ids go
    // out the same way the server decodes them, rather than via `bodyUnsafeJson` which bypasses encode.
    const request = yield* HttpClientRequest.post("/api/resolve").pipe(
      HttpClientRequest.schemaBodyJson(ResolveArgs)({ documentId, decision }),
    );
    const response = yield* client.execute(request);
    if (response.status === 409) {
      return yield* Effect.fail(
        new ResolveAlreadyDecided({
          message: "This finding has already been decided — refreshing to the latest state.",
        }),
      );
    }
    if (response.status >= 400) {
      return yield* Effect.fail(
        new ResolveRequestFailed({
          status: response.status,
          message: `The decision could not be saved (HTTP ${response.status}).`,
        }),
      );
    }
    return yield* HttpClientResponse.schemaBodyJson(ResolveSuccess)(response);
  }).pipe(Effect.scoped);

// `role`, when supplied, narrows the server-side query/build to those roles (CON-1182) — omitted
// fetches every role, unchanged from before this filter existed. Passing an array `setUrlParams`
// value expands to repeated `?role=` keys, matching the endpoint's `Schema.Array(CurriculumRole)`.
export const listCurricula = (roles?: ReadonlyArray<CurriculumRole>) =>
  HttpClient.HttpClient.pipe(
    Effect.flatMap((client) =>
      client.execute(
        HttpClientRequest.get("/api/curricula").pipe(
          HttpClientRequest.setUrlParams(roles !== undefined ? { role: roles } : {}),
        ),
      ),
    ),
    Effect.flatMap(HttpClientResponse.schemaBodyJson(CurriculaList)),
    Effect.scoped,
  );

// One document's current identity tags + lifecycle status (the curriculum-tags ADR), for the
// doc-view header chips and edit dropdowns. Absent `tags`/`status` = untagged / no status yet — not
// an error. `setUrlParam` URL-encodes the slash-bearing id.
export const getCurriculumTags = (documentId: DocumentId) =>
  HttpClient.HttpClient.pipe(
    Effect.flatMap((client) =>
      client.execute(
        HttpClientRequest.get("/api/curricula/tags").pipe(
          HttpClientRequest.setUrlParam("documentId", documentId),
        ),
      ),
    ),
    Effect.flatMap(HttpClientResponse.schemaBodyJson(CurriculumTagsView)),
    Effect.scoped,
  );

// `POST /api/uploads` — ingest one ad-hoc chat upload (chat-context-attach). The dropped file's raw
// bytes are sent as the request body (no JSON envelope, no lossy text round-trip) with the filename
// in the `?name=` query param and the browser `File.type` in `?type=` (so the server labels the
// Files API upload with its real MIME rather than a guess); the server streams them to the Anthropic
// Files API and returns the minted `fileId` (plus echoed name) that the composer then references on
// the turn — the bytes are never stored server-side or re-sent on the turn (ADR-0055).
const UploadSuccess = Schema.Struct({ fileId: Schema.String, name: Schema.String });

// The oversize 413 (`UploadTooLarge`) carries the server's own `{ message }` so the composer can show
// the exact reason ("… is too large to attach (max 100 MB)") rather than a blanket failure.
const UploadTooLargeBody = Schema.Struct({ message: Schema.String });

// Any non-2xx upload response is a *tagged* failure (mirroring `ResolveRequestFailed`): the 413
// carries the server's message; other statuses get a generic one. Never falls through to a
// `UploadSuccess` decode that would surface as an opaque `ParseError`.
export class UploadRequestFailed extends Data.TaggedError("UploadRequestFailed")<{
  readonly status: number;
  readonly message: string;
}> {}

export const uploadDocument = (name: string, contentType: string, bytes: Uint8Array) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const response = yield* client.execute(
      HttpClientRequest.post("/api/uploads").pipe(
        HttpClientRequest.setUrlParam("name", name),
        HttpClientRequest.setUrlParam("type", contentType),
        HttpClientRequest.bodyUint8Array(bytes, "application/octet-stream"),
      ),
    );
    if (response.status === 413) {
      const body = yield* HttpClientResponse.schemaBodyJson(UploadTooLargeBody)(response);
      return yield* Effect.fail(new UploadRequestFailed({ status: 413, message: body.message }));
    }
    if (response.status >= 400) {
      return yield* Effect.fail(
        new UploadRequestFailed({
          status: response.status,
          message: `Couldn't upload "${name}" (HTTP ${response.status}).`,
        }),
      );
    }
    return yield* HttpClientResponse.schemaBodyJson(UploadSuccess)(response);
  }).pipe(Effect.scoped);
